import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

export function useProperty() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setProperties([]);
      setSelectedProperty(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const propertiesRef = collection(db, 'properties');
    const propertiesQuery = query(propertiesRef, where('ownerId', '==', user.uid));

    const unsubscribe = onSnapshot(propertiesQuery, (snapshot) => {
      const nextProperties = snapshot.docs.map((documentSnapshot) => ({
        id: documentSnapshot.id,
        ...documentSnapshot.data(),
      }));

      setProperties(nextProperties);
      setSelectedProperty((current) => {
        if (nextProperties.length === 0) {
          return null;
        }

        if (!current) {
          return nextProperties[0];
        }

        const matched = nextProperties.find((property) => property.id === current.id);
        return matched || nextProperties[0];
      });
      setLoading(false);
    });

    return unsubscribe;
  }, [user?.uid]);

  const addProperty = async (propertyData) => {
    if (!user?.uid) {
      throw new Error('You must be signed in to create a property.');
    }

    const propertiesRef = collection(db, 'properties');
    const docRef = await addDoc(propertiesRef, {
      ...propertyData,
      ownerId: user.uid,
      createdAt: new Date().toISOString(),
    });

    return { id: docRef.id, ...propertyData, ownerId: user.uid };
  };

  const updateProperty = async (propertyId, updates) => {
    if (!user?.uid) {
      throw new Error('You must be signed in to update a property.');
    }

    if (!propertyId) {
      throw new Error('Property ID is required.');
    }

    return updateDoc(doc(db, 'properties', propertyId), {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  };

  return { properties, selectedProperty, setSelectedProperty, addProperty, updateProperty, loading };
}
